import { useSignal } from "@preact/signals-react";
import { ErrorField } from "../../../../types/ErrorField";
import TextEdit from "../../../molecules/TextEdit";
import SelectTextField from "../../../molecules/SelectTextField";

import { toast } from "react-toastify";

import ATable from "../../../organisms/ATable";
import { ATableHeader } from "../../../organisms/ATable";
import { FaStar } from "react-icons/fa";
import NewTooltip from "../../../atoms/NewTooltip";
import { HiOutlineTrash } from "react-icons/hi2";

import PresentationModal from "../../../organisms/PresentationModal";
import { useEffect } from "react";
import { getErrorMessage } from "../../../../utils/formErrorUtils";
import useCities from "../../../../hooks/useCities";
import MiniLoading from "../../../ions/MiniLoading";
import { useNavigate } from "react-router-dom";

interface BankAccount {
    id?: string;
    bankAccountId?: string;
    bank: string;
    cityId: number;
    city?: string;
    agency: string;
    account: string;
    active: boolean;
    canRemove: boolean;
    canSetActive: boolean;
    default: boolean;
}

interface Bank {
    id: number;
    name: string;
    febraban_id: string;
}

interface Props {
    legalPersonId: string;
    provinces: any[];
    banks: Bank[];
    bankAccounts: BankAccount[];
}

type BankAccountForm = {
    bankId: string;
    cityId: number;
    agency: string;
    account: string;
    digit: string;
    active: boolean;
    province: string;
};

const BASE_URL = `${process.env.REACT_APP_PERSON_API_URL}/legal-persons`;
const API_KEY = process.env.REACT_APP_API_KEY || '';

function BankAccountFormTab({ legalPersonId, provinces, banks, bankAccounts }: Props): JSX.Element {
    const isModalOpen = useSignal<boolean>(false);
    const { fetchCities } = useCities();
    const navigate = useNavigate();

    const cities = useSignal<any[]>([]);
    const isLoadingForm = useSignal<boolean>(false);
    const rows = useSignal<BankAccount[]>(bankAccounts);

    const isSendingForm = useSignal<boolean>(false);
    const isRemoveModalOpen = useSignal<boolean>(false);
    const confirmBankAccountNumber = useSignal<string>("");
    const selectedBankAccount = useSignal<BankAccount | null>(null);
    const errorFields = useSignal<ErrorField[]>([]);

    const accountForm = useSignal<BankAccountForm>({
        bankId: "",
        cityId: 0,
        agency: "",
        account: "",
        active: true,
        digit: "",
        province: "",
    });

    const headers: ATableHeader[] = [
        {
            id: 'bank',
            title: 'Banco',
            canSort: true,
            field: 'bank',
            width: 'w-[250px]',
        },
        {
            id: 'city',
            title: 'Cidade',
            canSort: true,
            field: 'city',
            width: '250px',
        },
        {
            id: 'agency',
            title: 'Agência',
            canSort: true,
            field: 'agency',
            width: '150px',
            cssClass: 'w-[150px]',
        },
        {
            id: 'account',
            title: 'Conta',
            canSort: true,
            field: 'account',
            width: '150px',
            cssClass: 'w-[150px]',
        },
        {
            id: 'active',
            title: 'Ativa',
            canSort: false,
            field: 'active',
            width: '90px',
            cssClass: 'w-[90px]',
        },
        {
            id: 'action',
            title: '',
            canSort: false,
            width: '50px',
        },
    ];

    useEffect(() => {
        window.addEventListener("BankAccountFormTab:openModal", () => isModalOpen.value = true);

        return () => {
            window.removeEventListener("BankAccountFormTab:openModal", () => isModalOpen.value = true);
        };
    }, []);

    const handleInputChange = (field: keyof BankAccountForm, value: string | boolean) => {
        accountForm.value = {
            ...accountForm.value,
            [field]: value,
        };
    };
    const onlyDigits = (value: string) => value.replace(/\D/g, "");

    const onlyDigitOrX = (value: string) => value.replace(/[^0-9xX]/g, "").toUpperCase();
    const submitForm = async () => {
        isSendingForm.value = true;

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${BASE_URL}/${legalPersonId}/bank-accounts`, {
                method: 'POST',
                headers: {
                    'x-api-key': API_KEY || '',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    ...accountForm.value,
                    cityId: parseInt(accountForm.value.cityId as unknown as string),
                }),
            });

            if (response.ok) {
                toast.success('Conta bancária cadastrada com sucesso!');
                isModalOpen.value = false;
                deactivateAllRows();
                const data = await response.json();
                createAccountRow(data);
                resetForm();
            } else if (response.status === 401) {
                toast.error('A sessão expirou. Faça login novamente.');
                navigate('/login');
            } else if (response.status === 409) {
                const { errors, message } = await response.json();
                toast.error(message);
                errorFields.value = errors ? errors.map((error: any) => ({ fieldName: error.field, message: error.message })) : [];
            } else {
                toast.error("Ocorreu um erro na comunicação com o servidor!\nTente novamente mais tarde.");
            }
        } catch (error) {
            toast.error("Ocorreu um erro na comunicação com o servidor!\nTente novamente mais tarde.");
        }
        isSendingForm.value = false;
    };

    const createAccountRow = (data: any) => {
        const bank = banks.find((bank) => bank.id == data.bank_id);
        const city = cities.value.find((city) => city.id == accountForm.value.cityId);
        const province = provinces.find((province) => province.initials == accountForm.value.province);

        rows.value = [...rows.value, {
            bankAccountId: data.id,
            agency: data.agency,
            account: `${data.account}-${data.digit}`,
            cityId: data.city_id,
            active: data.active,
            canRemove: data.canRemove,
            canSetActive: data.canSetActive,
            default: data.default,
            bank: `${bank?.name} (${bank?.febraban_id.toString().padStart(3, '0')})`,
            city: `${province?.initials} - ${city?.name}`,
        }];
    };
    const resetForm = () => {
        accountForm.value = {
            bankId: "",
            cityId: 0,
            agency: "",
            account: "",
            active: true,
            digit: "",
            province: "",
        };
        errorFields.value = [];
    };

    const removeBankAccount = async (bankAccountId: string) => {
        isSendingForm.value = true;
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${BASE_URL}/${legalPersonId}/bank-accounts/${bankAccountId}`, {
                method: 'DELETE',
                headers: {
                    'x-api-key': API_KEY || '',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                toast.success("Conta bancária removida com sucesso!");
                rows.value = rows.value.filter((row) => row.bankAccountId !== bankAccountId);
                isRemoveModalOpen.value = false;
            } else {
                toast.error("Não foi possível remover a conta bancária");
            }
        } catch (error) {
            toast.error("Erro ao remover conta bancária");
        }
        isSendingForm.value = false;
    };

    const activateBankAccount = async (bankAccountId: string) => {
        isSendingForm.value = true;
        selectedBankAccount.value = rows.value.find((row) => row.bankAccountId === bankAccountId) || null;
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${BASE_URL}/${legalPersonId}/bank-account-activation`, {
                method: 'POST',
                headers: {
                    'x-api-key': API_KEY || '',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    bankAccountId,
                }),
            });

            if (response.ok) {
                toast.success("Conta bancária ativada com sucesso!");
                deactivateAllRows();

                rows.value = rows.value.map((row) => {
                    if (row.bankAccountId === bankAccountId) {
                        return { ...row, default: true, canSetActive: false, canRemove: false };
                    }
                    return row;
                });
            } else {
                toast.error("Erro ao ativar conta bancária");
            }
        } catch (error) {
            toast.error("Erro ao ativar conta bancária");
        }
        selectedBankAccount.value = null;
        isSendingForm.value = false;
    };

    const deactivateAllRows = () => {
        rows.value = rows.value.map((row) => ({ ...row, default: false, canSetActive: true, canRemove: true }));
    };

    const fetchCitiesWrapper = async (province: string) => {
        try {
            isLoadingForm.value = true;
            cities.value = await fetchCities(province);
        } catch (error) {
            toast.error("Erro ao carregar as cidades. Tente novamente!");
        } finally {
            isLoadingForm.value = false;
        }
    };

    const renderRows = (rows: BankAccount[]) => {
        return rows.map((row, index) => {
            return (
                <tr key={index}
                    className={`group ${index % 2 === 0 ? 'bg-slate-100' : ''} hover:bg-slate-300 hover:shadow-md border-b border border-gray-300`}>
                    <td className="p-2">{row.bank}</td>
                    <td className="p-2">{row.city}</td>
                    <td className="p-2" align="center">{row.agency}</td>
                    <td className="p-2" align="center">{row.account}</td>
                    <td className="p-2" align="center">

                        {row.canSetActive &&
                            <button 
                                id={`btn-activate-bank-${row.bankAccountId}`}
                                onClick={() => activateBankAccount(row.bankAccountId!)}
                                disabled={isSendingForm.value}>
                                {isSendingForm.value && row.bankAccountId === selectedBankAccount.value?.bankAccountId ?
                                    <MiniLoading /> :

                                    <NewTooltip content="Ativar conta" alignment="left">
                                        <FaStar className="text-gray-500 text-xl hover:text-amber-500" />
                                    </NewTooltip>
                                }
                            </button>}

                        {row.default &&
                            <FaStar className="text-yellow-500 text-xl" />
                        }

                    </td>
                    <td className="p-2">
                        {row.canRemove &&
                            <NewTooltip content="Remover conta bancária" alignment="left">
                                <button
                                    id={`btn-remove-bank-${row.bankAccountId}`}
                                    className="text-red-500 text-lg hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                    onClick={() => {
                                        isRemoveModalOpen.value = true;
                                        selectedBankAccount.value = row;
                                        confirmBankAccountNumber.value = "";
                                    }}
                                >
                                    <HiOutlineTrash />
                                </button>
                            </NewTooltip>
                        }
                    </td>
                </tr>
            );
        });
    };

    return (
        <div className="p-2 pt-4 mx-4">
            <ATable
                headers={headers}
                fieldSorted={"bank"}
                orderSorted={"ASC"}
                autoResize={true}
                canSelectAll={false}
            >
                {renderRows(rows.value)}
            </ATable>

            <PresentationModal
                id="bank-account-modal"
                show={isModalOpen.value}
                onClose={() => {
                    isModalOpen.value = false;
                    resetForm();
                }}
                header="Nova conta bancária"
                isLoading={isSendingForm.value}
                loadingMessage="Aguarde, salvando..."
                footer={
                    <div className="flex justify-end gap-2 pt-4">
                        <button
                            id="btn-cancel-bank-modal"
                            onClick={() => {
                                isModalOpen.value = false;
                                resetForm();
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            id="btn-save-bank-account"
                            onClick={submitForm}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                            Salvar
                        </button>
                    </div>
                }
            >
                <div className="space-y-4 p-4">
                    <SelectTextField
                        id="bank-select"
                        label="Banco"
                        value={accountForm.value.bankId}
                        onChange={(e) => handleInputChange("bankId", e.target.value)}
                        items={banks.map((bank: Bank) => ({
                            label: bank.febraban_id ? `${bank.name} (${bank.febraban_id.toString().padStart(3, '0')})`
                                : bank.name,
                            value: bank.id.toString(),
                        }))}
                        errorMessage={getErrorMessage(errorFields.value, "bankId")}
                        required
                    />

                    <div className="grid grid-cols-6 gap-4">
                        <TextEdit
                            id="agency"
                            label="Agência"
                            className="col-span-2"
                            maxLength={4}
                            value={accountForm.value.agency}
                            onChange={(e) => handleInputChange("agency", onlyDigits(e.target.value))}
                            errorMessage={getErrorMessage(errorFields.value, "agency")}
                            required
                        />
                        <TextEdit
                            id="account"
                            label="Conta Corrente"
                            className="col-span-3"
                            value={accountForm.value.account}
                            onChange={(e) => handleInputChange("account", onlyDigits(e.target.value))}
                            maxLength={15}
                            errorMessage={getErrorMessage(errorFields.value, "account")}
                            required
                        />
                        <TextEdit
                            id="account-digit"
                            label="Digito"
                            className="col-span-1"
                            value={accountForm.value.digit}
                            onChange={(e) => handleInputChange("digit", onlyDigitOrX(e.target.value))}
                            maxLength={1}
                            errorMessage={getErrorMessage(errorFields.value, "digit")}
                            required
                        />
                    </div>

                    <div className="grid grid-cols-8 gap-4">
                        <SelectTextField
                            id="province"
                            name='province'
                            label="UF"
                            value={accountForm.value.province}
                            items={provinces.map((province) => ({
                                label: province.name,
                                value: province.initials,
                            }))}
                            className="col-span-3"
                            onChange={(e) => {
                                handleInputChange("province", e.target.value);
                                fetchCitiesWrapper(e.target.value);
                            }}
                        />

                        <SelectTextField
                            id="cityId"
                            name='cityId'
                            label="Cidade"
                            value={accountForm.value.cityId}
                            items={cities.value.map((city) => ({
                                label: city.name,
                                value: city.id,
                            }))}
                            isLoading={isLoadingForm.value}
                            className="col-span-5"
                            onChange={(e) => handleInputChange("cityId", e.target.value)}
                            errorMessage={getErrorMessage(errorFields.value, "cityId")}
                        />
                    </div>
                </div>
            </PresentationModal>

            <PresentationModal
                id="bank-account-remove-modal"
                show={isRemoveModalOpen.value}
                loadingMessage="Aguarde, removendo..."
                onClose={() => isRemoveModalOpen.value = false}
                header={`Remover conta bancária`}
                isLoading={isSendingForm.value}
            >
                <div className="p-4">
                    <p className="mb-4 text-xl font-medium text-gray-700">Tem certeza que deseja remover a conta bancária abaixo?</p>

                    <div className="mb-4 p-4 bg-white rounded-lg shadow-md border border-gray-200">
                        <p><b>Banco:</b> {selectedBankAccount.value?.bank}</p>
                        <p><b>Agência:</b> {selectedBankAccount.value?.agency}</p>
                        <p><b>Conta Corrente:</b> {selectedBankAccount.value?.account}</p>
                    </div>

                    <div className="flex items-center gap-4">
                        <p>Digite o número da conta para confirmar a remoção:</p>
                        <TextEdit
                            id="confirm-account-number"
                            className="w-60"
                            label="Número da conta"
                            value={confirmBankAccountNumber.value}
                            onChange={(e) => confirmBankAccountNumber.value = e.target.value}
                        />
                    </div>

                    <div className="flex justify-end gap-2 pt-4">
                        <button
                            id="btn-cancel-remove-bank"
                            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                            onClick={() => isRemoveModalOpen.value = false}
                        >
                            Cancelar
                        </button>
                        <button
                            id="btn-confirm-remove-bank"
                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={confirmBankAccountNumber.value !== selectedBankAccount.value?.account.split("-")[0]}
                            onClick={() => removeBankAccount(selectedBankAccount.value?.bankAccountId!)}
                        >
                            Remover
                        </button>
                    </div>
                </div>
            </PresentationModal>

        </div>
    );
}

export default BankAccountFormTab;